import { FC, useCallback, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import type { UseQueryOptions } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { OneLineText } from '@/components/OneLineText';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { displayFullName } from '@/lib/utils';
import {
  filePackageExportedMessage,
  getFilePackageDownload,
  getFilePackages,
  useFilePackageFilterOptions,
} from '@/services/filePackageService';
import type {
  FilePackageListItem,
  FilePackageListQueryParams,
  FilePackageListResponse,
  FilePackageStatus,
} from '@/types/filePackage.types';

const SERVICE_TYPE_LABELS: Record<FilePackageListItem['serviceType'], string> = {
  PROCESS_SERVICE: 'Process Service',
  HOUSE_INSPECTION: 'Home Inspection',
  DUE_DILIGENCE_INQUIRY: 'Due Diligence',
  DELIVERY: 'Delivery',
};

const STATUS_BADGES: Record<FilePackageStatus, { label: string; className: string }> = {
  IN_PROGRESS: { label: 'In Progress', className: 'bg-[#EDF5FF] text-primary hover:bg-[#EDF5FF]' },
  READY: { label: 'Ready', className: 'bg-[#D5F4E3] text-[#1B8652] hover:bg-[#D5F4E3]' },
  FAILED: { label: 'Failed', className: 'bg-[#FDE2E2] text-[#B42318] hover:bg-[#FDE2E2]' },
};

const toList = (value: unknown) => (Array.isArray(value) && value.length ? (value as string[]) : undefined);

// The presigned URL sets Content-Disposition, so a plain link saves the ZIP
// under the package name without fetching it through the app.
const startDownload = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const Downloads: FC = () => {
  const [exportingId, setExportingId] = useState<string | null>(null);
  const { data: filterOptions } = useFilePackageFilterOptions();

  const apiGet = useCallback((params?: unknown, options?: { query?: UseQueryOptions }) => {
    const raw = (params ?? {}) as Record<string, unknown>;
    const query: FilePackageListQueryParams = {
      clientId: toList(raw.clientId),
      fileNumber: toList(raw.fileNumber),
      caseNumber: toList(raw.caseNumber),
      page: raw.pageNo as number | undefined,
      pageSize: raw.pageSize as number | undefined,
      sortField: raw.sortField as FilePackageListQueryParams['sortField'],
      sortDirection: raw.sortDirection as FilePackageListQueryParams['sortDirection'],
    };
    return getFilePackages(query, options as { query?: Partial<UseQueryOptions<FilePackageListResponse>> });
  }, []);

  const handleExport = async (filePackage: FilePackageListItem) => {
    setExportingId(filePackage.filePackageId);
    try {
      const { data, error } = await getFilePackageDownload(filePackage.filePackageId);
      if (!data) {
        displayError(error);
        return;
      }
      startDownload(data.downloadUrl, data.packageName);
      toast({ description: filePackageExportedMessage(data.packageName), duration: 5000 });
    } finally {
      setExportingId(null);
    }
  };

  const searchConfig: DataTableSearchConfig[] = useMemo(
    () => [
      {
        name: 'clientId',
        label: 'Search Client',
        placeholder: 'Search',
        fieldType: 'MULTISELECT',
        multiSelectOptions: filterOptions?.clients ?? [],
        labelToDisplay: { dropdown: 'label1', selectedOption: 'label1' },
      },
      {
        name: 'fileNumber',
        label: 'Search File Number',
        placeholder: 'Enter File Number',
        fieldType: 'MULTISELECT',
        multiSelectOptions: filterOptions?.fileNumbers ?? [],
      },
      {
        name: 'caseNumber',
        label: 'Search Case Number',
        placeholder: 'Enter Case Number',
        fieldType: 'MULTISELECT',
        multiSelectOptions: filterOptions?.caseNumbers ?? [],
      },
    ],
    [filterOptions]
  );

  // Columns per the DGR-1829 design: no package name / created-on columns,
  // and an unlabelled download column.
  const columns: ColumnDef<FilePackageListItem>[] = useMemo(
    () => [
      {
        accessorKey: 'client',
        header: 'Client #',
        cell: ({ row }) => <span className="min-w-[80px] mr-4">{row.original.client.code}</span>,
      },
      {
        accessorKey: 'fileNumber',
        header: 'File Number',
        cell: ({ row }) => <span className="min-w-[110px] mr-4">{row.original.fileNumber}</span>,
      },
      {
        accessorKey: 'caseNumber',
        header: 'Case Number',
        cell: ({ row }) => <span className="min-w-[110px] mr-4">{row.original.caseNumber}</span>,
      },
      {
        accessorKey: 'serviceType',
        header: 'Service Type',
        cell: ({ row }) => (
          <span className="min-w-[110px] mr-4">{SERVICE_TYPE_LABELS[row.original.serviceType]}</span>
        ),
      },
      {
        accessorKey: 'createdBy',
        header: 'Created By',
        cell: ({ row }) => (
          <OneLineText text={displayFullName(row.original.createdBy) ?? '-'} className="min-w-[120px] mr-4" />
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const badge = STATUS_BADGES[row.original.status];
          return (
            <Badge className={`${badge.className} min-w-[104px] justify-center whitespace-nowrap mr-4`}>
              {badge.label}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'actions',
        header: '',
        size: 60,
        cell: ({ row }) =>
          row.original.status === 'READY' ? (
            <button
              type="button"
              title="Export"
              aria-label={`Export ${row.original.packageName}`}
              disabled={exportingId === row.original.filePackageId}
              className="flex items-center mr-4 text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={(e) => {
                e.stopPropagation();
                void handleExport(row.original);
              }}
            >
              <Download className="h-4 w-4" />
            </button>
          ) : null,
      },
    ],
    // handleExport only reads exportingId through its setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exportingId]
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <DataTable toolbar={{ toolBarTitle: 'Download Package' }} columns={columns} apiGet={apiGet} search={searchConfig} />
    </div>
  );
};

export default Downloads;
